import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { ArrowLeft, ListChecks } from 'lucide-react';
import { toast } from 'sonner';
import { jobsAPI, JOB_TYPE_LABELS } from '../lib/jobsAPI';
import { clientsAPI } from '../lib/clientsAPI';
import { authAPI } from '../lib/api';

export default function NewJob() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [templatePreview, setTemplatePreview] = useState([]);
  const [useTemplate, setUseTemplate] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    client_id: '',
    job_type: '',
    period_label: '',
    period_start: '',
    period_end: '',
    due_at: '',
    assignee_id: '',
    reviewer_id: '',
    priority: 'medium',
    internal_notes: '',
    client_notes: '',
  });

  useEffect(() => {
    loadClients();
    loadUsers();
  }, []);

  const loadClients = async () => {
    try {
      const response = await clientsAPI.list({ status: 'active' });
      setClients(response.data);
    } catch (error) {
      console.error('Failed to load clients');
    }
  };

  const loadUsers = async () => {
    try {
      // For MVP, current user. Extend to all org members when an endpoint exists.
      const response = await authAPI.getMe();
      setUsers([response.data.user]);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const set = (key, value) => setFormData((f) => ({ ...f, [key]: value }));

  const handleJobTypeChange = async (jobType) => {
    set('job_type', jobType);
    try {
      const response = await jobsAPI.checklistTemplate(jobType);
      setTemplatePreview(response.data.labels || []);
    } catch (error) {
      setTemplatePreview([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.client_id || !formData.job_type) {
      toast.error('Title, client and job type are required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: formData.title,
        client_id: formData.client_id,
        job_type: formData.job_type,
        period_label: formData.period_label || null,
        period_start: formData.period_start || null,
        period_end: formData.period_end || null,
        due_at: formData.due_at ? new Date(formData.due_at).toISOString() : null,
        assignee_id: formData.assignee_id || null,
        reviewer_id: formData.reviewer_id || null,
        priority: formData.priority,
        use_template: useTemplate,
        internal_notes: formData.internal_notes || null,
        client_notes: formData.client_notes || null,
      };
      const response = await jobsAPI.create(payload);
      toast.success('Job created');
      navigate(`/jobs/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create job');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/jobs')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Jobs
        </Button>
      </div>

      <div>
        <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="new-job-title">
          New Job
        </h1>
        <p className="text-muted-foreground mt-1">Create an accounting job against a client</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Job Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Job Title *</Label>
              <Input
                id="title"
                placeholder="e.g., FY2025 Company Tax Return — Acme Pty Ltd"
                value={formData.title}
                onChange={(e) => set('title', e.target.value)}
                required
                data-testid="job-title-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="client">Client *</Label>
                <Select value={formData.client_id} onValueChange={(v) => set('client_id', v)}>
                  <SelectTrigger id="client" data-testid="job-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.display_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="job_type">Job Type *</Label>
                <Select value={formData.job_type} onValueChange={handleJobTypeChange}>
                  <SelectTrigger id="job_type" data-testid="job-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(JOB_TYPE_LABELS).map(([k, label]) => (
                      <SelectItem key={k} value={k}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="period_label">Period Label</Label>
                <Input
                  id="period_label"
                  placeholder="e.g., FY2025, Q1 2025"
                  value={formData.period_label}
                  onChange={(e) => set('period_label', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="period_start">Period Start</Label>
                <Input id="period_start" type="date" value={formData.period_start} onChange={(e) => set('period_start', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="period_end">Period End</Label>
                <Input id="period_end" type="date" value={formData.period_end} onChange={(e) => set('period_end', e.target.value)} />
              </div>
            </div>

            {/* Checklist template preview — shown once a job type is picked */}
            {templatePreview.length > 0 && (
              <div className="rounded-lg border border-border bg-muted/50 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <ListChecks className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-foreground/90">
                    Default checklist for {JOB_TYPE_LABELS[formData.job_type]}
                  </span>
                </div>
                <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                  {templatePreview.map((label, i) => (
                    <li key={i}>{label}</li>
                  ))}
                </ul>
                <div className="flex items-center gap-2 pt-1">
                  <Checkbox
                    id="use_template"
                    checked={useTemplate}
                    onCheckedChange={(c) => setUseTemplate(c === true)}
                  />
                  <Label htmlFor="use_template" className="text-sm font-normal cursor-pointer">
                    Add these checklist items to the job
                  </Label>
                </div>
              </div>
            )}

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="due_at">Due Date</Label>
                <Input id="due_at" type="date" value={formData.due_at} onChange={(e) => set('due_at', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select value={formData.priority} onValueChange={(v) => set('priority', v)}>
                  <SelectTrigger id="priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="assignee">Assign To</Label>
                <Select value={formData.assignee_id} onValueChange={(v) => set('assignee_id', v)}>
                  <SelectTrigger id="assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reviewer">Reviewer</Label>
              <Select value={formData.reviewer_id} onValueChange={(v) => set('reviewer_id', v)}>
                <SelectTrigger id="reviewer"><SelectValue placeholder="No reviewer" /></SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="internal_notes">Internal Notes (staff only)</Label>
              <Textarea id="internal_notes" rows={2} value={formData.internal_notes} onChange={(e) => set('internal_notes', e.target.value)} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="client_notes">Client-Facing Notes</Label>
              <Textarea id="client_notes" rows={2} value={formData.client_notes} onChange={(e) => set('client_notes', e.target.value)} />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => navigate('/jobs')}>Cancel</Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={submitting} data-testid="create-job-submit">
                {submitting ? 'Creating...' : 'Create Job'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
